import React, { useState } from "react";
import { overviewReportData } from "../App";
import Title from "../components/common/Title";
import FilterSelector from "../components/overview-report/FilterSelector";
import { alert } from "devextreme/ui/dialog";
import Server from "../api/Server";
import notify from "devextreme/ui/notify";
import Overview from "../components/overview-report/Overview";
import {
  format,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfYear,
  endOfYear,
  subDays,
  subWeeks,
  subMonths,
  subYears,
} from "date-fns";
import { getNavigatorLanguage } from "../utils";
import { saveAs } from "file-saver";

export let overOrShorReportData = [];

const OverviewReport = () => {
  const [reportStore, setReportStore] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [state, setState] = useState(overviewReportData);
  const [isExporting, setIsExporting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const {
      unixTimeEnd,
      unixTimeStart,
      currencyId,
      pointOfSaleIds,
      warehouseIds,
      warehouseRegionIds,
      varianceReasonCodeIds,
      overOrShort,
    } = state;

    if (!unixTimeEnd || !unixTimeStart || !currencyId) {
      alert("Start date, End date and Currency are required", "Notification");
      return;
    }

    setIsLoading(true);

    const params = {
      startDateTime: `${format(unixTimeStart, "yyyy-MM-dd")} 00:00:00`,
      endDateTime: `${format(unixTimeEnd, "yyyy-MM-dd")} 23:59:59`,
      currencyId,
    };

    if (pointOfSaleIds.length) {
      params.pointOfSaleIds = pointOfSaleIds.map((e) => e).toString();
    }

    if (warehouseIds.length) {
      params.warehouseIds = warehouseIds.map((e) => e).toString();
    }

    if (warehouseRegionIds.length) {
      params.warehouseRegionIds = warehouseRegionIds.map((e) => e).toString();
    }

    if (varianceReasonCodeIds.length) {
      params.varianceReasonCodeIds = varianceReasonCodeIds
        .map((e) => e)
        .toString();
    }

    if (overOrShort) {
      params.overOrShort = overOrShort;
    }

    try {
      const {
        data: { data },
      } = await Server.getOverAndShortReport(params);
      setReportStore(data);
      overOrShorReportData = data;
      setIsLoading(false);
    } catch (err) {
      if (err.response) {
        const {
          response: { data },
        } = err;
        if (data) {
          notify(`${data.status.errorDescription}`, "error", 3000);
        } else {
          notify("Error getting reports data, please try again", "error", 3000);
        }
      } else {
        notify("Error getting reports data, please try again", "error", 3000);
      }

      setIsLoading(false);
    }
  };

  const exportReport = async () => {
    try {
      const {
        unixTimeEnd,
        unixTimeStart,
        currencyId,
        pointOfSaleIds,
        warehouseIds,
        warehouseRegionIds,
        varianceReasonCodeIds,
        overOrShort,
      } = state;

      setIsExporting(true);

      const params = {
        startDateTime: `${format(unixTimeStart, "yyyy-MM-dd")} 00:00:00`,
        endDateTime: `${format(unixTimeEnd, "yyyy-MM-dd")} 23:59:59`,
        currencyId,
      };

      if (pointOfSaleIds.length) {
        params.pointOfSaleIds = pointOfSaleIds.map((e) => e).toString();
      }

      if (warehouseIds.length) {
        params.warehouseIds = warehouseIds.map((e) => e).toString();
      }

      if (warehouseRegionIds.length) {
        params.warehouseRegionIds = warehouseRegionIds.map((e) => e).toString();
      }

      if (varianceReasonCodeIds.length) {
        params.varianceReasonCodeIds = varianceReasonCodeIds
          .map((e) => e)
          .toString();
      }

      if (overOrShort) {
        params.overOrShort = overOrShort;
      }

      const { data, headers } = await Server.exportOverAndShortReportToCSV(
        params
      );
      const blob = new Blob([data], {
        type: headers["content-type"],
      });

      saveAs(blob, `over-or-short-${new Date().getTime()}.csv`);

      notify("Report exported successfully", "success", 3000);
      setIsExporting(false);
    } catch (err) {
      if (err.response) {
        const {
          response: { data },
        } = err;
        if (data) {
          notify(`${data.status.errorDescription}`, "error", 3000);
        } else {
          notify("Error exporting report, please try again", "error", 3000);
        }
      } else {
        notify("Error exporting report, please try again", "error", 3000);
      }

      setIsExporting(false);
    }
  };

  const onValueChanged = (event, name) => {
    const { value } = event;

    if (name === "dateRange") {
      setQuickDate(value);
    } else {
      setState({ ...state, [name]: value });
    }
  };

  const setQuickDate = (value) => {
    if (value) {
      if (value === "today") {
        setState({
          ...state,
          dateRange: value,
          unixTimeStart: Date.now(),
          unixTimeEnd: Date.now(),
        });
      }

      if (value === "current week") {
        const locale = getNavigatorLanguage();
        const start = startOfWeek(Date.now(), { locale });
        const end = endOfWeek(Date.now(), { locale });

        setState({
          ...state,
          dateRange: value,
          unixTimeStart: start,
          unixTimeEnd: end,
        });
      }

      if (value === "current month") {
        const start = startOfMonth(Date.now());
        const end = endOfMonth(Date.now());

        setState({
          ...state,
          dateRange: value,
          unixTimeStart: start,
          unixTimeEnd: end,
        });
      }

      if (value === "current year") {
        const start = startOfYear(Date.now());
        const end = endOfYear(Date.now());

        setState({
          ...state,
          dateRange: value,
          unixTimeStart: start,
          unixTimeEnd: end,
        });
      }

      if (value === "yesterday") {
        setState({
          ...state,
          dateRange: value,
          unixTimeStart: subDays(Date.now(), 1),
          unixTimeEnd: subDays(Date.now(), 1),
        });
      }

      if (value === "last week") {
        const week = subWeeks(Date.now(), 1);
        const locale = getNavigatorLanguage();
        const start = startOfWeek(week, { locale });
        const end = endOfWeek(week, { locale });
        setState({
          ...state,
          dateRange: value,
          unixTimeStart: start,
          unixTimeEnd: end,
        });
      }

      if (value === "last month") {
        const month = subMonths(Date.now(), 1);
        const start = startOfMonth(month);
        const end = endOfMonth(month);
        setState({
          ...state,
          dateRange: value,
          unixTimeStart: start,
          unixTimeEnd: end,
        });
      }

      if (value === "last year") {
        const year = subYears(Date.now(), 1);
        const start = startOfYear(year);
        const end = endOfYear(year);
        setState({
          ...state,
          dateRange: value,
          unixTimeStart: start,
          unixTimeEnd: end,
        });
      }

      if (value === "last 15 days") {
        setState({
          ...state,
          dateRange: value,
          unixTimeStart: subDays(Date.now(), 14),
          unixTimeEnd: Date.now(),
        });
      }

      if (value === "last 30 days") {
        setState({
          ...state,
          dateRange: value,
          unixTimeStart: subDays(Date.now(), 29),
          unixTimeEnd: Date.now(),
        });
      }

      if (value === "last 60 days") {
        setState({
          ...state,
          dateRange: value,
          unixTimeStart: subDays(Date.now(), 59),
          unixTimeEnd: Date.now(),
        });
      }

      if (value === "last 90 days") {
        setState({
          ...state,
          dateRange: value,
          unixTimeStart: subDays(Date.now(), 89),
          unixTimeEnd: Date.now(),
        });
      }
    } else {
      setState({
        ...state,
        dateRange: value,
        unixTimeStart: null,
        unixTimeEnd: null,
      });
    }
  };

  return (
    <section className='mt-4'>
      <Title pageTitle={"Over/Short Report"} />
      <FilterSelector
        formData={state}
        handleSubmit={handleSubmit}
        onValueChanged={onValueChanged}
      />
      <Overview
        isLoading={isLoading}
        reportStore={reportStore}
        isExporting={isExporting}
        exportReport={exportReport}
      />
    </section>
  );
};

export default OverviewReport;
